import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSiteContent } from '../context/SiteContentContext';
import { homeIcons, legalDisclaimer } from '../siteContent';
import CmsImage from '../components/CmsImage';

const Icon = ({ name, className, style }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} style={style} />;
};

const HowWeHelp = () => {
  const { howwehelp: c } = useSiteContent();

  return (
    <div className="bg-white" data-testid="how-we-help-page">
      {/* Dark editorial hero */}
      <section className="relative bg-[#003366] text-white overflow-hidden">
        <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#E74C3C]/20 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold text-white/60 mb-5">{c.hero.eyebrow}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-4xl leading-[1.05]">{c.hero.heading}</h1>
          <p className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">{c.hero.subtext}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            {c.pillars.map((p) => (
              <a key={p.title} href={`#${p.title.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-full bg-white/10 hover:bg-white/20 border border-white/15 px-5 py-2 text-sm font-medium transition-colors">
                {p.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Alternating editorial pillar rows */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20 lg:space-y-28">
          {c.pillars.map((p, i) => {
            const meta = homeIcons.pillars[i] || { icon: 'Circle', accent: '#E74C3C' };
            const flip = i % 2 === 1;
            return (
              <div key={p.title} id={p.title.toLowerCase().replace(/\s+/g, '-')} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-24" data-testid={`hwh-pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className={flip ? 'lg:order-2' : ''}>
                  <div className="relative">
                    <CmsImage src={p.image} alt={p.title} className="rounded-[2rem] w-full h-[340px] lg:h-[420px] object-cover shadow-xl" />
                    <div className="absolute -bottom-5 left-6 rounded-2xl px-5 py-3 shadow-lg flex items-center gap-3" style={{ backgroundColor: meta.accent }}>
                      <Icon name={meta.icon} className="w-6 h-6 text-white" />
                      <span className="font-heading font-bold text-white">0{i + 1}</span>
                    </div>
                  </div>
                </div>
                <div className={flip ? 'lg:order-1' : ''}>
                  <span className="text-sm uppercase tracking-[0.2em] font-semibold" style={{ color: meta.accent }}>Pillar 0{i + 1}</span>
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#1A1A1A] mt-3">{p.title}</h2>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-6">
                    {(p.points || []).map((pt) => (
                      <div key={pt} className="flex items-start gap-2.5">
                        <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
                        <span className="text-[#444]">{pt}</span>
                      </div>
                    ))}
                  </div>
                  {p.note ? <p className="mt-6 text-sm text-[#666] italic border-l-2 pl-4" style={{ borderColor: meta.accent }}>{p.note}</p> : null}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Difference band */}
      <section className="py-20 bg-[#FDFBF9] border-y border-[#EAE5E0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#1A1A1A] text-center mb-12">{c.difference.heading}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {c.difference.items.map((title, i) => (
              <div key={title} className="flex items-start gap-3 bg-white rounded-2xl border border-[#EAE5E0] p-5">
                <Icon name={homeIcons.differences[i] || 'Star'} className="w-5 h-5 text-[#E74C3C] mt-0.5 flex-shrink-0" />
                <span className="font-heading font-semibold text-sm text-[#1A1A1A] leading-snug">{title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A]">{c.ctaHeading}</h2>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/membership"><Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6 text-base group">Join Free Community <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /></Button></Link>
            <Link to="/events"><Button variant="outline" className="rounded-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-8 py-6 text-base">See Upcoming Events</Button></Link>
          </div>
          <p className="mt-12 text-xs text-[#666] leading-relaxed max-w-3xl mx-auto">{legalDisclaimer}</p>
        </div>
      </section>
    </div>
  );
};

export default HowWeHelp;
